/**
 * src/components/Financial/modules/GannPlanetaryPriceModule.jsx
 */
import React, { useMemo } from "react";
import * as Astro from "../../../astronomy.js";
import { Target, Info, ArrowLeftRight } from "lucide-react";
import {
  PLANET_SYMBOLS,
  PLANET_COLORS,
  PLANETS,
} from "../../../utils/constants.js";

/**
 * Planetary Channel Lines 描画レイヤー
 */
export const GannPlanetaryPriceLayer = function (props) {
  const {
    show,
    timeline,
    coords,
    stats,
    scale = 100, // デフォルト値を100に
    base = 0,
    mirror = false,
    isHeliocentric,
    isSidereal,
    isDarkMode,
    selectedPlanets = new Set(),
  } = props;

  if (!show || !timeline || timeline.length === 0 || !coords || !coords.getY)
    return null;

  const { getX, getY, pad, iH, actualWidth } = coords;

  const linesData = useMemo(() => {
    const results = [];
    if (selectedPlanets.size === 0) return results;

    const mirrorFactor = mirror ? -1 : 1;
    const cycleStep = 360 * scale;
    const baseOffset = scale * 360 * base;

    selectedPlanets.forEach((planet) => {
      const points = [];

      timeline.forEach((d, i) => {
        const date = d.timestamp ? new Date(d.timestamp) : new Date(d.Date);
        const lon = isHeliocentric
          ? Astro.getHeliocentricLongitude(planet, date)
          : Astro.getPlanetLongitude(planet, date, isSidereal);

        if (lon === null || lon === undefined) return;

        const basePrice = mirrorFactor * lon * scale + baseOffset;
        const kStart = Math.floor((stats.min - basePrice) / cycleStep);
        const kEnd = Math.ceil((stats.max - basePrice) / cycleStep);

        for (let k = kStart; k <= kEnd; k++) {
          const price = basePrice + k * cycleStep;
          if (!points[k]) points[k] = [];
          points[k].push({ xIdx: i, price: price });
        }
      });

      Object.keys(points).forEach((k) => {
        if (points[k].length > 1) {
          results.push({
            id: `${planet}-${k}`,
            planet,
            color: PLANET_COLORS[planet],
            symbol: PLANET_SYMBOLS[planet],
            pathPoints: points[k],
          });
        }
      });
    });

    return results;
  }, [
    timeline,
    selectedPlanets,
    scale,
    base,
    mirror,
    isHeliocentric,
    isSidereal,
    stats.min,
    stats.max,
  ]);

  // クリップパスのID（他と被らないように定義）
  const clipId = "gann-planetary-clip";

  return (
    <g className="planetary-channel-lines">
      <defs>
        {/* チャート表示エリアのみを切り抜く定義 */}
        <clipPath id={clipId}>
          <rect
            x={pad.l}
            y={pad.t}
            width={actualWidth - pad.l - pad.r}
            height={iH}
          />
        </clipPath>
      </defs>

      {/* クリップパスを適用したグループ */}
      <g clipPath={`url(#${clipId})`}>
        {linesData.map((line) => {
          const dStr = line.pathPoints
            .map((pt, idx) => {
              const x = getX(pt.xIdx);
              const y = getY(pt.price);
              return `${idx === 0 ? "M" : "L"} ${x} ${y}`;
            })
            .join(" ");

          const lastPt = line.pathPoints[line.pathPoints.length - 1];
          const lastX = getX(lastPt.xIdx);
          const lastY = getY(lastPt.price);

          return (
            <g key={line.id}>
              <path
                d={dStr}
                fill="none"
                stroke={line.color}
                strokeWidth="1.5"
                strokeOpacity="0.6"
                strokeDasharray="4 2"
              />
              {/* ラベルをクリップ範囲の右端ギリギリに配置 */}
              <g
                transform={`translate(${Math.min(lastX + 5, actualWidth - pad.r - 40)}, ${lastY})`}
              >
                <text
                  fontSize="12"
                  fill={line.color}
                  fontWeight="bold"
                  dominantBaseline="middle"
                >
                  {line.symbol}
                </text>
                <text
                  x="18"
                  fontSize="9"
                  fill={line.color}
                  opacity="0.8"
                  dominantBaseline="middle"
                >
                  {Math.round(lastPt.price).toLocaleString()}
                </text>
              </g>
            </g>
          );
        })}
      </g>
    </g>
  );
};

export const GannPlanetaryPriceSettingsUI = function (props) {
  const {
    show,
    setShow,
    selectedPlanets,
    setSelectedPlanets,
    scale,
    setScale,
    base,
    setBase,
    mirror,
    setMirror,
    isDarkMode,
  } = props;

  const isDark =
    isDarkMode === true || isDarkMode === "true" || isDarkMode === "dark";

  const btnSt = (active) => ({
    padding: "6px 20px",
    fontSize: "11px",
    fontWeight: "bold",
    borderRadius: "4px",
    cursor: "pointer",
    background: active ? "#2c2d96" : isDark ? "#2d2c33" : "#fff",
    color: active ? "#fff" : isDark ? "#ccc" : "#64748b",
    border:
      "1px solid " + (active ? "#2c2d96" : isDark ? "#374151" : "#cbd5e1"),
  });

  return (
    <div
      style={{
        background: isDark ? "#1c1f26" : "#f8fafc",
        padding: "20px",
        borderRadius: "12px",
        border: "1px solid " + (isDark ? "#2d313d" : "#e2e8f0"),
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            fontWeight: "bold",
            color: isDark ? "#fff" : "#2c2d96",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Target size={18} />
          <span>惑星チャネルライン</span>
        </div>
        <button onClick={() => setShow(!show)} style={btnSt(show)}>
          {show ? "ON" : "OFF"}
        </button>
      </div>

      <div
        style={{
          opacity: show ? 1 : 0.5,
          pointerEvents: show ? "auto" : "none",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "15px",
            marginBottom: "20px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <label
              style={{
                fontSize: "11px",
                color: isDark ? "#94a3b8" : "#64748b",
              }}
            >
              スケール (Scale)
            </label>
            <div style={{ display: "flex", gap: "4px" }}>
              {[1000, 100, 10, 1, 0.1].map((v) => (
                <button
                  key={v}
                  onClick={() => setScale(v)}
                  style={btnSt(scale === v)}
                >
                  {v}x
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <label
              style={{
                fontSize: "11px",
                color: isDark ? "#94a3b8" : "#64748b",
              }}
            >
              Base Multiple
            </label>
            <input
              type="number"
              value={base}
              onChange={(e) => setBase(parseInt(e.target.value) || 0)}
              style={{
                padding: "6px",
                width: "60px",
                background: isDark ? "#0b0e14" : "#fff",
                color: isDark ? "#fff" : "#333",
                border: "1px solid " + (isDark ? "#374151" : "#cbd5e1"),
                borderRadius: "4px",
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <label
              style={{
                fontSize: "11px",
                color: isDark ? "#94a3b8" : "#64748b",
              }}
            >
              Mirror
            </label>
            <button onClick={() => setMirror(!mirror)} style={btnSt(mirror)}>
              <ArrowLeftRight size={14} />
            </button>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {PLANETS.slice(0, 10).map((p) => (
            <button
              key={p}
              onClick={() => {
                const next = new Set(selectedPlanets);
                if (next.has(p)) next.delete(p);
                else next.add(p);
                setSelectedPlanets(next);
              }}
              style={{
                ...btnSt(selectedPlanets.has(p)),
                border:
                  "1.5px solid " +
                  (selectedPlanets.has(p)
                    ? PLANET_COLORS[p]
                    : isDark
                      ? "#374151"
                      : "#e2e8f0"),
                color: selectedPlanets.has(p)
                  ? isDark
                    ? "#fff"
                    : PLANET_COLORS[p]
                  : isDark
                    ? "#94a3b8"
                    : "#64748b",
              }}
            >
              {PLANET_SYMBOLS[p]} {p}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
